import { useCMSPageField } from '@/cms/fields'
import { CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useIntl } from '@/intl'
import { FullScreenGallery } from '@/scopes/common/components/FullScreenGallery'
import { ImageCarousel } from '@/scopes/common/components/ImageCarousel'
import { useShowScrollButton } from '@/scopes/common/hooks/useShowScrollButton'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IconButton } from '@sunwebgroup/ui-components/uds'
import useEmblaCarousel from 'embla-carousel-react'
import { startTransition, useMemo, useState } from 'react'
import { BeyondSlopes } from './BeyondSlopes/BeyondSlopes'
import { Lifts } from './Lifts/Lifts'
import styles from './SkiArea.module.css'
import { Slopes } from './Slopes/Slopes'

export function SkiArea() {
  const intl = useIntl()
  const skiAreas = useCMSPageField('DestinationPage.skiArea')
  const [activeIndex, setActiveIndex] = useState(0)
  const [showGallery, setShowGallery] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)

  const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true })
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = useShowScrollButton(emblaApi)

  const activeArea = skiAreas ? skiAreas[activeIndex] : null

  const mapImages = useMemo(() => activeArea?.mapImages?.map(src => ({
    src,
    alt: '',
    type: CMSMediaType.IMAGE,
  })), [activeArea])

  if (!skiAreas || skiAreas.length === 0) {
    return null
  }

  return (
    <div className={styles.skiAreaSection}>
      <div className={styles.tabBarWrapper}>
        {!prevBtnDisabled && (
          <div className={`${styles.scrollButtonWrapper} ${styles.scrollButtonWrapperLeft}`}>
            <IconButton className={styles.scrollButton} onClick={onPrevButtonClick} size="S" variant="secondary" glyph="ChevronLeftSmall" aria-label={intl.formatMessage({ id: 'destination_ski_area_prev_tab' })} />
          </div>
        )}
        <div className={styles.tabBarSlider} ref={emblaRef}>
          <div className={styles.tabBar} role="tablist">
            {skiAreas.map((area, index) => {
              const isActive = index === activeIndex
              return (
                <Button
                  key={area.name}
                  role="tab"
                  aria-selected={isActive}
                  variant={isActive ? 'transparentPrimary' : 'transparentNeutral'}
                  size="M"
                  className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  {`${area.isMainRegion ? intl.formatMessage({ id: 'destination_ski_area_region' }) : intl.formatMessage({ id: 'destination_ski_area_subregion' })} ${area.name}`}
                </Button>
              )
            })}
          </div>
        </div>
        {!nextBtnDisabled && (
          <div className={`${styles.scrollButtonWrapper} ${styles.scrollButtonWrapperRight}`}>
            <IconButton className={styles.scrollButton} onClick={onNextButtonClick} size="S" variant="secondary" glyph="ChevronRightSmall" aria-label={intl.formatMessage({ id: 'destination_ski_area_next_tab' })} />
          </div>
        )}
      </div>
      <div className={styles.tabContent}>
        {activeArea && (
          <div className={styles.tabDetails}>
            <Slopes data={activeArea.slopes} />
            <BeyondSlopes data={activeArea.beyondSlopes} />
            <Lifts data={activeArea.lifts} />
          </div>
        )}
        {mapImages && (
          <div className={styles.tabMap}>
            <IconButton
              className={styles.mapZoomButton}
              glyph="Magnifier"
              size="M"
              aria-label={intl.formatMessage({ id: 'destination_ski_area_zoom_map' })}
              onClick={() => {
                setActiveSlide(0)
                startTransition(() => setShowGallery(true))
              }}
            />
            <ImageCarousel
              onClick={(slide) => {
                setActiveSlide(slide)
                startTransition(() => {
                  setShowGallery(true)
                })
              }}
              images={mapImages}
            />
            {showGallery && (
              <FullScreenGallery
                zoom
                onClose={() => setShowGallery(false)}
                startFromIndex={activeSlide}
                open={showGallery}
                media={mapImages}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
